import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { useDeleteDocument } from 'api/documents/use-delete-document';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import type { IDocumentControlProps } from 'components/documents/document-item/components/document-control/types';

export const DocumentControl: React.FC<IDocumentControlProps> = ({ documentId }) => {
  const { t } = useTranslation();

  const isAllow = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.customer_manager,
    UserRole.transport_manager,
    UserRole.storekeeper,
  ]);

  const [modal, contextHolder] = Modal.useModal();
  const [isActiveControl, setIsActiveControl] = useState(false);
  const { mutateAsync: deleteDocumentApi } = useDeleteDocument();

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteDocumentApi({ documentId });
        deactivateWithTimeout();
      },
    });
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirm();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  if (!isAllow) return null;

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'delete',
              label: t('common:delete'),
              icon: <DeleteOutlined />,
            },
          ],
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
    </>
  );
};
